package com.dotdrives.web.pages.settings

import com.dotdrives.controller.SettingsController
import com.dotdrives.controller.SettingsUiState
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.INVITABLE_ROLES
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import react.FC
import react.Props
import react.dom.aria.AriaPressed
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.email

external interface InviteUserDialogProps : Props {
    var controller: SettingsController
    var state: SettingsUiState
    var onClose: () -> Unit
}

private data class InviteRoleOption(val role: String, val icon: String)

// Derived from the shared INVITABLE_ROLES, not a second hand-maintained list -- see that
// constant's doc comment. This is the single place that would need to change (plus
// SettingsController.setInviteRole's guard, which reads the same constant) for a role to
// become invitable; adding a role to INVITABLE_ROLES alone is not enough to offer it here.
private val INVITE_ROLE_OPTIONS = INVITABLE_ROLES.map { role -> InviteRoleOption(role, roleIcon(role)) }

val InviteUserDialog = FC<InviteUserDialogProps> { props ->
    val state = props.state

    div {
        className = ClassName("settings-dialog-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onClose() }

        div {
            className = ClassName("settings-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Invite a teammate"
            onClick = { it.stopPropagation() }

            div { className = ClassName("settings-dialog-title"); +"Invite a teammate" }

            state.inviteError?.let { message ->
                DotBanner {
                    this.message = message
                    className = "settings-inline-banner"
                }
            }

            div {
                className = ClassName("settings-form-field")
                div { className = ClassName("settings-form-label"); +"Email" }
                input {
                    type = InputType.email
                    value = state.inviteEmail
                    placeholder = "teammate@example.org"
                    ariaLabel = "Teammate email"
                    autoFocus = true
                    onChange = { event -> props.controller.setInviteEmail(event.currentTarget.value) }
                }
            }

            div {
                className = ClassName("settings-form-field")
                div { className = ClassName("settings-form-label"); +"Role" }
                div {
                    className = ClassName("settings-role-options")
                    INVITE_ROLE_OPTIONS.forEach { option ->
                        val selected = option.role == state.inviteRole
                        button {
                            type = ButtonType.buttonType
                            className = ClassName(
                                "settings-role-option" + if (selected) " settings-role-option-selected" else "",
                            )
                            ariaPressed = selected.unsafeCast<AriaPressed>()
                            onClick = { props.controller.setInviteRole(option.role) }
                            span { +option.icon }
                            div {
                                className = ClassName("settings-role-option-copy")
                                div { className = ClassName("settings-role-option-name"); +roleLabel(option.role) }
                                div { className = ClassName("settings-role-option-desc"); +roleDescription(option.role) }
                            }
                            if (selected) {
                                span { className = ClassName("settings-role-option-check"); +"✓" }
                            }
                        }
                    }
                }
            }

            if (state.inviteRole == "member") {
                div {
                    className = ClassName("settings-dialog-note")
                    +"Standard users get a personal goal — set it from the Users list once they've joined."
                }
            }

            div {
                className = ClassName("settings-dialog-actions")
                DotButton {
                    text = "Cancel"
                    variant = DotButtonVariant.Secondary
                    onClick = props.onClose
                }
                DotButton {
                    text = "Send invite ✉"
                    loading = state.inviteSending
                    onClick = props.controller::sendInvite
                }
            }
        }
    }
}
